import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import { useNavigate } from 'react-router-dom';
import WF_ListSearchHeader from '../../common/listSearchHeader/WF_ListSearchHeader';
import DataTable from 'react-data-table-component';
import { getPropertyListColumns, propertyListCustomStyles } from './PropertyListColumns';
import { WfWorkplace } from 'src/api/wf/models/WfWorkplace';
import { useTranslation } from 'react-i18next';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import { PaginationHeader } from '../../common/paginationHeader/PaginationHeader';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { useWfAlertHistoryStore } from 'src/api/wf/stores/WfAlertHistoryStore';
import { LoadingSpinner } from '../../common/loading/LoadingSpinner';

function WF_4_4_1_PropertiesList() {
  const { t } = useTranslation();
  const { patchFilterPagingParameters, filterPagingParameters } = useWfFilterStore();
  const paginatedWorkplace = useWfWorkplaceStore((state) => state.paginatedWorkplace);
  const { fetchPaginatedWorkplaces } = useWfWorkplaceService();
  const { setSelectedWorkplace, setPaginatedWorkplace } = useWfWorkplaceStore();
  const navigate = useNavigate();
  const { patchHistorySearchParams } = useWfAlertHistoryStore();
  const { filterParameters } = useWfFilterStore();

  const [isSearched, setIsSearched] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    setSelectedWorkplace(null);

    const hasMandatoryFilters =
      Boolean(filterParameters.branch) && Boolean(filterParameters.salesOffice);

    const hasCustomerFilter = Boolean(filterParameters.customerNumber);

    if ((hasMandatoryFilters || hasCustomerFilter) && !isSearched) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = async (newPage: number) => {
    setIsLoading(true);
    setIsTimeout(false);
    setPaginatedWorkplace(null);
    setSelectedWorkplace(null);
    patchFilterPagingParameters({ pageNumber: newPage - 1 });
    try {
      await fetchPaginatedWorkplaces(newPage - 1);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectedWorkplace = (selectedRow: WfWorkplace | null) => {
    setSelectedWorkplace(selectedRow);
    if (selectedRow) {
      patchHistorySearchParams({
        customerNumber: selectedRow.customerId,
        branchCode: selectedRow.branchCode,
        salesOfficeCode: selectedRow.salesOfficeCode,
        organizationCode: selectedRow.organizationCode,
        buildingName: selectedRow.name,
      });
    } else {
      patchHistorySearchParams({
        customerNumber: null,
        branchCode: null,
        salesOfficeCode: null,
        organizationCode: null,
        buildingName: null,
      });
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setIsTimeout(false);
    setPaginatedWorkplace(null);
    setSelectedWorkplace(null);
    patchFilterPagingParameters({ pageNumber: 0 });
    try {
      await fetchPaginatedWorkplaces(0);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      } else {
        console.error(error);
      }
    } finally {
      // タイムアウトしても結果・タイムアウト表示のセクションを必ず描画する
      setIsSearched(true);
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => getPropertyListColumns(t, navigate, setSelectedWorkplace),
    [t, navigate, setSelectedWorkplace]
  );

  return (
    <div className="mt-2 pb-4">
      <WF_ListSearchHeader onSearch={handleSearch} />

      {isLoading && <LoadingSpinner />}

      {!isLoading && isSearched && (
        <>
          {paginatedWorkplace?.pageElements && paginatedWorkplace?.pageElements.length > 0 && (
            <PaginationHeader
              page={filterPagingParameters?.pageNumber + 1}
              perPage={paginatedWorkplace?.pageSize}
              total={paginatedWorkplace?.totalElements ?? 0}
              onPageChange={(p) => handlePageChange(p)}
            />
          )}
          <div className="overflow-x-auto pb-2">
            <DataTable
              selectableRows
              selectableRowsSingle
              selectableRowsHighlight
              dense
              noDataComponent={t(isTimeout ? 'wf.common.searchTimeout' : 'wf.common.noResults')}
              data={paginatedWorkplace?.pageElements ?? []}
              columns={columns}
              customStyles={propertyListCustomStyles}
              onSelectedRowsChange={(state) =>
                handleSelectedWorkplace(state.selectedRows[0] || null)
              }
            />
          </div>
        </>
      )}
    </div>
  );
}

export default WF_4_4_1_PropertiesList;
